import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Tag,
  Button,
  Space,
  Card,
  Spin,
  Typography,
  Select,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  BarChartOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import api from '../services/api';
import { authService } from '../services/auth';
import { formatCurrency } from '../utils/currency';
import { formatDate } from '../utils/dateUtils';
import { formatLocalizedPercent } from '../utils/numberFormatting';
import { formatWarehouseQuantity } from '../modules/warehouse/warehouseQuantityConversion';

const { Title, Text } = Typography;
const { Option } = Select;

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface BatchRow {
  id: string;
  batchNumber: string;
  productVariantId: string;
  variantSku: string;
  variantName: string;
  pzDocumentId: string | null;
  pzNumber: string | null;
  purchaseDate: string;
  quantityUnit: string | null;
  bought: number;
  boughtPcs?: number | null;
  boughtM2?: number | null;
  boughtM3?: number | null;
  inStock: number;
  inStockPcs?: number | null;
  inStockM2?: number | null;
  inStockM3?: number | null;
  soldQty: number;
  soldQtyPcs?: number | null;
  soldQtyM2?: number | null;
  soldQtyM3?: number | null;
  unitCostPLN: number;
  totalCostPLN: number;
  cogsRealizedPLN: number;
  stockValuePLN: number;
  saleRevenuePLN: number | null;
  batchMarginPLN: number | null;
  marginPercent: number | null;
  status: 'IN_PROGRESS' | 'CLOSED';
}

interface ConsumedOrderRow {
  orderId: string;
  orderNumber: string;
  quantityUnit: string | null;
  qtyFromBatch: number;
  cogsFromBatchPLN: number;
  saleRevenueFromBatchPLN: number | null;
  marginFromBatchPLN: number | null;
  perUnitMarginPLN: number | null;
  orderRevenuePLN: number;
  orderTotalCostPLN: number;
  orderMarginPLN: number;
}

// ─── API ─────────────────────────────────────────────────────────────────────

async function fetchBatchProfitability(statusFilter?: string): Promise<BatchRow[]> {
  const params: Record<string, string> = {};
  if (statusFilter) params.status = statusFilter;
  const response = await api.get('/reports/batch-profitability', { params });
  return response.data;
}

async function fetchBatchConsumingOrders(batchId: string): Promise<ConsumedOrderRow[]> {
  const response = await api.get(`/reports/batch-profitability/${batchId}`);
  return response.data;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function marginColor(value: number): string | undefined {
  if (value > 0) return '#3f8600';
  if (value < 0) return '#cf1322';
  return undefined;
}

function renderPln(value: number | null | undefined): React.ReactNode {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  const color = marginColor(value);
  return color
    ? <span style={{ color }}>{formatCurrency(value, 'PLN')}</span>
    : formatCurrency(value, 'PLN');
}

function renderPercent(value: number | null | undefined, language?: string): React.ReactNode {
  if (value === null || value === undefined) return <Text type="secondary">—</Text>;
  const color = marginColor(value);
  const text = formatLocalizedPercent(value, language);
  return color ? <span style={{ color }}>{text}</span> : text;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  embedded?: boolean;
}

const BatchProfitability: React.FC<Props> = ({ embedded }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['inventory', 'common']);
  const feedback = useFeedback();

  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // Expanded rows: batchId → orders
  const [expandedOrders, setExpandedOrders] = useState<Record<string, ConsumedOrderRow[]>>({});
  const [loadingOrders, setLoadingOrders] = useState<Record<string, boolean>>({});

  // Redirect if no margin visibility — defensive guard in addition to nav gating
  useEffect(() => {
    if (!authService.canViewFinancialReports()) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  async function load() {
    setLoading(true);
    try {
      const data = await fetchBatchProfitability(statusFilter);
      setBatches(data);
    } catch (err: any) {
      feedback.error(getApiErrorMessage(err, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }

  async function handleExpand(expanded: boolean, record: BatchRow) {
    if (!expanded) return;
    if (expandedOrders[record.id]) return; // already loaded
    setLoadingOrders(prev => ({ ...prev, [record.id]: true }));
    try {
      const orders = await fetchBatchConsumingOrders(record.id);
      setExpandedOrders(prev => ({ ...prev, [record.id]: orders }));
    } catch (err: any) {
      feedback.error(getApiErrorMessage(err, t, 'inventory:error.fetchFailed'));
    } finally {
      setLoadingOrders(prev => ({ ...prev, [record.id]: false }));
    }
  }

  // ── Sub-table: consuming orders ──────────────────────────────────────────
  const orderColumns = [
    {
      title: t('inventory:batchProfitability.orderNumber'),
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string, record: ConsumedOrderRow) => (
        <Button type="link" size="small" onClick={() => navigate(`/app/orders/${record.orderId}`)}>
          {text}
        </Button>
      ),
    },
    {
      title: t('inventory:batchProfitability.qtyFromBatch'),
      dataIndex: 'qtyFromBatch',
      key: 'qtyFromBatch',
      align: 'right' as const,
      render: (value: number, record: ConsumedOrderRow) => (
        record.quantityUnit
          ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language)
          : value
      ),
    },
    {
      title: t('inventory:batchProfitability.cogsFromBatch'),
      dataIndex: 'cogsFromBatchPLN',
      key: 'cogsFromBatchPLN',
      align: 'right' as const,
      render: (v: number) => formatCurrency(v, 'PLN'),
    },
    {
      title: t('inventory:batchProfitability.saleFromBatch'),
      dataIndex: 'saleRevenueFromBatchPLN',
      key: 'saleRevenueFromBatchPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.marginFromBatch'),
      dataIndex: 'marginFromBatchPLN',
      key: 'marginFromBatchPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.perUnitMargin'),
      dataIndex: 'perUnitMarginPLN',
      key: 'perUnitMarginPLN',
      align: 'right' as const,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.orderMargin'),
      dataIndex: 'orderMarginPLN',
      key: 'orderMarginPLN',
      align: 'right' as const,
      render: renderPln,
    },
  ];

  // ── Main table: batches ──────────────────────────────────────────────────
  const batchColumns = [
    {
      title: t('inventory:batchProfitability.batchNumber'),
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 130,
      render: (_: string, record: BatchRow) => record.pzNumber || record.batchNumber,
    },
    {
      title: t('inventory:batchProfitability.variant'),
      key: 'variant',
      render: (_: unknown, record: BatchRow) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.variantSku}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{record.variantName}</Text>
        </Space>
      ),
    },
    {
      title: t('inventory:batchProfitability.purchaseDate'),
      dataIndex: 'purchaseDate',
      key: 'purchaseDate',
      width: 120,
      render: (d: string) => formatDate(d),
    },
    {
      title: t('inventory:batchProfitability.bought'),
      dataIndex: 'bought',
      key: 'bought',
      align: 'right' as const,
      width: 80,
      render: (value: number, record: BatchRow) => (
        record.quantityUnit
          ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language)
          : value
      ),
    },
    {
      title: t('inventory:batchProfitability.sold'),
      dataIndex: 'soldQty',
      key: 'soldQty',
      align: 'right' as const,
      width: 80,
      render: (value: number, record: BatchRow) => (
        record.quantityUnit
          ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language)
          : value
      ),
    },
    {
      title: t('inventory:batchProfitability.inStock'),
      dataIndex: 'inStock',
      key: 'inStock',
      align: 'right' as const,
      width: 80,
      render: (value: number, record: BatchRow) => (
        record.quantityUnit
          ? formatWarehouseQuantity(value, record.quantityUnit, i18n.language)
          : value
      ),
    },
    {
      title: t('inventory:batchProfitability.cogs'),
      dataIndex: 'cogsRealizedPLN',
      key: 'cogsRealizedPLN',
      align: 'right' as const,
      width: 130,
      render: (v: number) => formatCurrency(v, 'PLN'),
    },
    {
      title: t('inventory:batchProfitability.saleRevenue'),
      dataIndex: 'saleRevenuePLN',
      key: 'saleRevenuePLN',
      align: 'right' as const,
      width: 130,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.batchMargin'),
      dataIndex: 'batchMarginPLN',
      key: 'batchMarginPLN',
      align: 'right' as const,
      width: 130,
      render: renderPln,
    },
    {
      title: t('inventory:batchProfitability.marginPercent'),
      dataIndex: 'marginPercent',
      key: 'marginPercent',
      align: 'right' as const,
      width: 100,
      render: (value: number | null | undefined) => renderPercent(value, i18n.language),
    },
    {
      title: t('inventory:batchProfitability.status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (s: BatchRow['status']) => (
        <Tag color={s === 'CLOSED' ? 'default' : 'processing'}>
          {t(`inventory:batchProfitability.${s === 'CLOSED' ? 'statusClosed' : 'statusInProgress'}`)}
        </Tag>
      ),
    },
  ];

  const expandedRowRender = (record: BatchRow) => {
    if (loadingOrders[record.id]) {
      return <Spin size="small" style={{ display: 'block', margin: '16px auto' }} />;
    }
    const orders = expandedOrders[record.id] || [];
    return (
      <Table
        dataSource={orders}
        columns={orderColumns}
        rowKey="orderId"
        size="small"
        pagination={false}
        locale={{ emptyText: t('inventory:batchProfitability.noOrders') }}
      />
    );
  };

  return (
    <div style={embedded ? undefined : { padding: '24px' }}>
      {!embedded && (
        <div
          style={{
            marginBottom: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/app/inventory')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>
              <BarChartOutlined style={{ marginRight: 8 }} />
              {t('inventory:batchProfitability.title')}
            </Title>
          </Space>
          <Space>
            <Select
              allowClear
              placeholder={t('inventory:batchProfitability.filterStatus')}
              style={{ width: 180 }}
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
            >
              <Option value="IN_PROGRESS">
                {t('inventory:batchProfitability.statusInProgress')}
              </Option>
              <Option value="CLOSED">
                {t('inventory:batchProfitability.statusClosed')}
              </Option>
            </Select>
          </Space>
        </div>
      )}

      {embedded && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Select
            allowClear
            placeholder={t('inventory:batchProfitability.filterStatus')}
            style={{ width: 180 }}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
          >
            <Option value="IN_PROGRESS">
              {t('inventory:batchProfitability.statusInProgress')}
            </Option>
            <Option value="CLOSED">
              {t('inventory:batchProfitability.statusClosed')}
            </Option>
          </Select>
        </div>
      )}

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('inventory:batchProfitability.attributionNote')}
      />

      <Card>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
          </div>
        ) : (
          <Table
            dataSource={batches}
            columns={batchColumns}
            rowKey="id"
            size="small"
            pagination={{
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              defaultPageSize: 25,
            }}
            expandable={{
              expandedRowRender,
              onExpand: handleExpand,
              // Invalidate stale cached orders when row collapses so a
              // re-expand always fetches fresh data
              onExpandedRowsChange: (expandedKeys) => {
                setExpandedOrders(prev => {
                  const next: typeof prev = {};
                  expandedKeys.forEach(k => {
                    if (prev[k as string]) next[k as string] = prev[k as string];
                  });
                  return next;
                });
              },
            }}
            locale={{ emptyText: t('inventory:batchProfitability.noBatches') }}
          />
        )}
      </Card>
    </div>
  );
};

export default BatchProfitability;
